<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="vaultAccountRef"
      :busy="isBusy"
      :items="vaultAccountList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
              @click="onSelectVaultAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectVaultAccount(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeleteVaultAccount(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <!-- Pagination -->
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      :ok-disabled="isLoading"
      size="md"
      :title="modalTitle"
      @hidden="resetModal"
      @ok="handleSubmit"
    >
      <validation-observer ref="vaultAccountRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="12">
              <SelectField
                id="branch"
                v-model="vaultAccountFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Branch -->

            <!-- Account -->
            <b-col md="12">
              <SelectField
                id="account"
                v-model="vaultAccountFormData.accountNumber"
                :label="$t('Account')"
                :options="accountTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Account')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :loading="isLoadingAccount"
              />
            </b-col>
            <!-- Account -->

            <b-col md="12">
              <SelectField
                id="currency-code"
                v-model="vaultAccountFormData.currencyCode"
                :label="$t('Currency')"
                :reduce="option => option"
                :options="currencyCodeOptions"
                item="label"
                :placeholder="$t('Select currency')"
                :readonly="isEditable"
              />
            </b-col>
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="md"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchVaultAccounts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Branch -->
        <b-col md="12">
          <SelectField
            id="branch"
            v-model="filter.branchID"
            :label="$t('Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            :error-name="$t('Branch')"
            rules="required"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Branch -->

        <!-- Account -->
        <b-col md="12">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            :label="$t('Account')"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            :error-name="$t('Account')"
            rules="required"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account -->

        <b-col md="12">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            :label="$t('Currency')"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            :placeholder="$t('Select currency')"
          />
        </b-col>

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal, BSpinner,
  BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import { operationPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    TablePagination,
    TableEntries,
    Button,
    BTable,
    BSpinner,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    SelectField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      sortBy: 'branch',
      isBusy: true,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create Vault Account'),
      isReadonly: false,
      required,
      showSpinner: false,
      isRequired: false,
      isLoading: false,
      isLoadingAccount: false,
      sortDesc: false,
      toAmount,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'branchName', label: 'Branch' },
        { key: 'currencyCode', sortable: true, label: 'Currency' },
        { key: 'actions', label: 'Actions' },
      ],
      accountTypeOptions: [],
      vaultAccountFormData: {
        branchCode: '',
        accountNumber: '',
      },
      cashVolumeOptions: [
        { label: 'Unrequired', value: 1 },
        { label: 'Required', value: 2 },
      ],
      vaultAccountList: [],
      tellerNameOptions: [],
      filter: {
        branchID: '',
        accountNumber: '',
        currencyCode: '',
      },
      currencyCodeOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  mounted() {
    this.getAccounts()
    this.fetchVaultAccounts()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getVaultAccountList: 'operations/getVaultAccountList',
      createVaultAccount: 'operations/createVaultAccountAction',
      updateVaultAccount: 'operations/updateVaultAccountAction',
      getAccountCategoryByCode: 'account/getAccountCategoryByCode',
      deleteVaultAccount: 'operations/deleteBusinessAutomation',
      getAllCurrencies: 'account/getAllCurrenciesAction',
    }),

    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: operationPermissions.VIEW_BUSINESS_AUTOMATION })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    onSelectVaultAccount(items, activity, val) {
      this.isEditable = true
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Vault Account') : this.$t('View Vault Account')
      this.vaultAccountFormData = { ...items }
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async vaultAccountAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createVaultAccount(this.vaultAccountFormData)
          : await this.updateVaultAccount(this.vaultAccountFormData)
        const { status } = response
        if (status === 200) {
          this.fetchVaultAccounts()
          this.hideModal('modal-lg')
          const yourRequest = this.$t('Your request to')
          const successText = this.$t('Vault Account was successfully')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.vaultAccountRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisVault = this.$t('this Vault Account?')
          this.confirm(this.vaultAccountAction, { text: `${areYouSure} ${this.buttonLabel} ${thisVault}` })
        }
      })
    },

    async fetchVaultAccounts() {
      try {
        this.isBusy = true
        const response = await this.getVaultAccountList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_BUSINESS_AUTOMATION,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.vaultAccountList = data.data
          this.totalCount = data.recordCount
        } else {
          this.vaultAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getAccounts() {
      try {
        this.isLoadingAccount = true
        const response = await this.getAccountCategoryByCode(10103000)
        const { data, status } = response
        if (status === 200 && data.data) {
          this.accountTypeOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingAccount = false
      }
    },
    async onDeleteVaultAccount(item) {
      const deleteVaultAccount = async () => {
        try {
          this.showSpinner = true
          const response = await this.deleteVaultAccount({ routeName: 'VaultAccount', id: item.id })
          const { status } = response
          if (status === 200) {
            this.fetchVaultAccounts()
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteVaultAccount, { text: this.$t('Are you sure you want to delete this Vault Account?') })
    },
    resetModal() {
      this.vaultAccountFormData = { ...null }
      this.isLoading = false
      this.isEditable = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create Vault Account')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchVaultAccounts()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
